import { useState } from 'react';
import { Button, Card, Group, Modal, Stack, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useRevokeShare, type Share } from '@/api/projects';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';

export interface SharesTableProps {
  projectId: string;
  shares: Share[];
}

export function SharesTable({ projectId, shares }: SharesTableProps) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const { date } = useFormat();
  const revoke = useRevokeShare(projectId);
  const [toRevoke, setToRevoke] = useState<Share | null>(null);

  const handleRevoke = async () => {
    if (!toRevoke) return;
    try {
      await revoke.mutateAsync(toRevoke.public_id);
      setToRevoke(null);
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  if (shares.length === 0) {
    return (
      <Card>
        <Text fz={14} c={brand.textMuted}>
          {t('share.empty')}
        </Text>
      </Card>
    );
  }

  return (
    <Card p={0}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('share.email')}</Table.Th>
            <Table.Th>{t('share.role')}</Table.Th>
            <Table.Th>{t('share.expires')}</Table.Th>
            <Table.Th>{t('project.state')}</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {shares.map((s) => (
            <Table.Tr key={s.public_id}>
              <Table.Td>{s.user?.full_name ? `${s.user.full_name} (${s.email})` : s.email}</Table.Td>
              <Table.Td>{t(`share.roles.${s.role}`)}</Table.Td>
              <Table.Td>{s.expires_at ? date(s.expires_at) : t('share.noExpiry')}</Table.Td>
              <Table.Td>{s.is_live ? t('share.live') : t('share.revoked')}</Table.Td>
              <Table.Td>
                {s.is_live && (
                  <Button color="red" variant="light" size="xs" onClick={() => setToRevoke(s)}>
                    {t('share.revoke')}
                  </Button>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Modal opened={!!toRevoke} onClose={() => setToRevoke(null)} title={t('share.revoke')}>
        <Stack gap="md">
          <Text>{t('share.confirmRevoke', { email: toRevoke?.email })}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setToRevoke(null)}>
              {tc('common.cancel')}
            </Button>
            <Button color="red" loading={revoke.isPending} onClick={() => void handleRevoke()}>
              {t('share.revoke')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
